/**
 * useEnvironmentalMapLayers - Hook para inicializar capas reales de Environmental en el mapa
 *
 * @remarks
 * Carga datos REALES de árboles desde la API y los visualiza en el mapa usando MapLibre layers.
 * Reemplaza la carga de datos MOCK del SampleDataService.
 *
 * FASE B: Corrección de visibilidad - Solo inicialización de capas, NO UI de búsqueda.
 */

import { useEffect, useRef } from 'react';
import type { GeoJSONSource } from 'maplibre-gl';
import { mapEngine, isMapReadyForSources } from '../../../map-engine/MapEngineService';
import { treesApi } from '../../../trees/trees-api';

// Source and layer IDs
const TREES_SOURCE = 'env-trees-real-source';
const TREES_LAYER = 'env-trees-real';

// Colors following WESTEER palette
const TREE_COLOR = '#10B981'; // green-500

export interface UseEnvironmentalMapLayersOptions {
  projectId: string;
  visible: boolean;
}

/**
 * Hook que inicializa y gestiona capas reales de Environmental en el mapa
 */
export function useEnvironmentalMapLayers({
  projectId,
  visible,
}: UseEnvironmentalMapLayersOptions): void {
  const layersCreatedRef = useRef(false);

  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !visible || !projectId) return;

    const initializeLayers = (): void => {
      if (layersCreatedRef.current) return;
      // Guard: don't operate on map before style is loaded (Fase 2C — early map mount)
      if (!isMapReadyForSources(map)) return;
      layersCreatedRef.current = true;

      console.log('[useEnvironmentalMapLayers] Inicializando capas de Environmental...');

      // TREES
      if (!map.getSource(TREES_SOURCE)) {
        map.addSource(TREES_SOURCE, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        });
      }

      if (!map.getLayer(TREES_LAYER)) {
        map.addLayer({
          id: TREES_LAYER,
          type: 'circle',
          source: TREES_SOURCE,
          paint: {
            'circle-radius': [
              'interpolate',
              ['linear'],
              ['zoom'],
              10,
              4, // zoom 10: 4px
              15,
              8, // zoom 15: 8px
              18,
              12, // zoom 18: 12px
            ],
            'circle-color': TREE_COLOR,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#FFFFFF',
            'circle-opacity': 0.85,
          },
        });
        console.log('[useEnvironmentalMapLayers] ✓ Capa trees creada');
      }
    };

    const loadData = async (): Promise<void> => {
      try {
        console.log(`[useEnvironmentalMapLayers] Cargando árboles de proyecto: ${projectId}`);

        // Cargar árboles reales desde API
        const trees = await treesApi.getByProject(projectId);
        const treesGeoJSON = treesApi.toGeoJSON(trees);

        // Actualizar source con datos reales
        const treesSource = map.getSource(TREES_SOURCE) as GeoJSONSource;
        if (treesSource) {
          treesSource.setData(treesGeoJSON);
          console.log(
            `[useEnvironmentalMapLayers] ✓ Árboles cargados: ${treesGeoJSON.features.length} features`
          );
        }
      } catch (error) {
        console.error('[useEnvironmentalMapLayers] Error cargando datos:', error);
      }
    };

    // Inicializar capas y cargar datos.
    // Si el style aún no está listo (mapa creado desde cache), esperar el evento load.
    if (isMapReadyForSources(map)) {
      initializeLayers();
      loadData();
    } else {
      // map is MapLibreMap here (already checked !map above), cast needed for TS narrowing
      const safeMap = map as import('maplibre-gl').Map;
      const onLoad = (): void => { initializeLayers(); void loadData(); };
      safeMap.once('load', onLoad);
      return () => { safeMap.off('load', onLoad); };
    }

    // Cleanup al desmontar
    return () => {
      if (!map) return;

      console.log('[useEnvironmentalMapLayers] Limpiando capas...');

      // Remover layer
      if (isMapReadyForSources(map) && map.getLayer(TREES_LAYER)) map.removeLayer(TREES_LAYER);

      // Remover source
      if (isMapReadyForSources(map) && map.getSource(TREES_SOURCE)) map.removeSource(TREES_SOURCE);

      layersCreatedRef.current = false;
    };
  }, [projectId, visible]);
}
